import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Share2, MapPin, GraduationCap, Briefcase, Users, Globe } from 'lucide-react';
import { Interview } from '../types/interviews';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useAuth } from '../lib/auth-context';
import { ReadingProgress } from '../components/ReadingProgress';
import { fetchInterviewById } from '../lib/api';

const cleanContent = (content: string): string => {
  if (!content) return '';
  return content
    .replace(/style="[^"]*color:[^";]*;?[^"]*"/g, '')
    .replace(/style="[^"]*background-color:[^";]*;?[^"]*"/g, '')
    .replace(/style="[^"]*font-weight:[^";]*;?[^"]*"/g, '')
    .replace(/style="[^"]*text-decoration:\s*underline[^";]*;?[^"]*"/g, 'class="custom-underline"')
    .replace(/<\/?(?:b|strong)>/g, '')
    .replace(/<\/?em>/g, '')
    .replace(/<\/?u>/g, '')
    .replace(/<a[^>]*>(<img[^>]*>)<\/a>/g, '$1');
};

const formatSection = (section: { type: string; content: string }, interview: Interview | null) => {
  if (!section || !section.content) return '';
  const cleanedContent = cleanContent(section.content);

  switch (section.type) {
    case 'question':
      return `<div class="interview-question mb-6">
        <div class="relative bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 px-5 py-4 rounded-2xl border border-teal-100/60 dark:border-teal-800/30">
          <div class="absolute top-1/2 right-4 -translate-y-1/2 w-20 h-20 opacity-[0.04]">
            <img src="https://pvtistes.net/wp-content/uploads/2024/12/sign_fullcolor.png" alt="" class="w-full h-full object-contain dark:hidden" />
            <img src="https://pvtistes.net/wp-content/uploads/2025/02/sign_white.png" alt="" class="w-full h-full object-contain hidden dark:block" />
          </div>
          <p class="relative text-[15px] sm:text-base font-medium text-gray-900 dark:text-white leading-relaxed">
            ${cleanedContent}
          </p>
        </div>
      </div>`;
    case 'answer': {
      if (!interview?.interviewee) return '';
      const avatar = interview.interviewee.avatar || `https://ui-avatars.com/api/?name=${interview.interviewee.firstname}&background=random`;
      return `<div class="interview-answer mb-8 pl-0 sm:pl-4">
        <div class="flex items-start gap-3">
          <div class="hidden sm:block flex-shrink-0 mt-1">
            <img src="${avatar}" alt="${interview.interviewee.firstname}" class="w-10 h-10 rounded-full ring-2 ring-gray-200/50 dark:ring-gray-700/50" />
          </div>
          <div class="flex-1 prose prose-sm sm:prose-base dark:prose-invert bg-white dark:bg-gray-800/60 p-4 sm:p-5 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-sm">
            ${cleanedContent}
          </div>
        </div>
      </div>`;
    }
    default:
      return `<div class="mb-8">
        <div class="prose prose-base dark:prose-invert bg-gray-50 dark:bg-gray-800/50 p-4 rounded-2xl border border-gray-200/50 dark:border-gray-700/50">
          ${cleanedContent}
        </div>
      </div>`;
  }
};

function IntervieweeCard({ interview }: { interview: Interview }) {
  const interviewee = interview.interviewee;
  const avatar = interviewee.avatar || `https://ui-avatars.com/api/?name=${interviewee.firstname}&background=random`;

  const details = [
    { icon: MapPin, label: interviewee.location },
    { icon: Briefcase, label: interviewee.occupation },
    { icon: GraduationCap, label: interviewee.diploma },
    { icon: Globe, label: interviewee.destination },
  ].filter(d => d.label);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-sm overflow-hidden">
      <div className="bg-gradient-to-br from-teal-500 to-cyan-600 px-5 py-3 relative">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-white/80" />
          <span className="text-xs font-semibold text-white/90 uppercase tracking-wide">Pvtiste interviewe</span>
        </div>
      </div>
      <div className="px-5 pb-5 pt-4">
        <div className="w-14 h-14 rounded-xl ring-3 ring-white dark:ring-gray-800 shadow-md overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-gray-700">
          <img
            src={avatar}
            alt={interviewee.firstname}
            className="w-full h-full object-cover"
          />
        </div>
        <h3 className="text-base font-bold text-gray-900 dark:text-white mt-3">
          {interviewee.firstname} {interviewee.lastname}
        </h3>
        {interviewee.age > 0 && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{interviewee.age} ans</p>
        )}
        {details.length > 0 && (
          <div className="mt-4 space-y-2.5">
            {details.map((detail, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-teal-50 dark:bg-teal-900/20 flex items-center justify-center flex-shrink-0">
                  <detail.icon className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400" />
                </div>
                <span className="text-sm text-gray-700 dark:text-gray-300 leading-tight">{detail.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function InterviewDetailPage() {
  const { user, userInfo } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [interview, setInterview] = React.useState<Interview | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!user) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  React.useEffect(() => {
    const fetchInterview = async () => {
      if (!id || !userInfo) return;
      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchInterviewById(id, userInfo);
        setInterview(data);
      } catch (err) {
        console.error('Error fetching interview:', err);
        setError("Impossible de charger l'interview pour le moment");
      } finally {
        setIsLoading(false);
      }
    };
    if (user) {
      fetchInterview();
    }
    window.scrollTo(0, 0);
  }, [id, user, userInfo]);

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: interview?.title,
          text: interview?.shortDescription,
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        alert('Lien copie dans le presse-papier !');
      }
    } catch (err) {
      console.error('Error sharing:', err);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col pb-mobile">
      <Navbar
        onLogin={() => {}}
        onAdminClick={() => {}}
        onHomeClick={() => {}}
        isAdminMode={false}
      />

      <ReadingProgress color="teal" />

      <div className="fixed top-16 left-0 right-0 z-40 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm border-b border-gray-200/50 dark:border-gray-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <button
              onClick={() => navigate('/interviews')}
              className="flex items-center gap-2 px-3 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Retour aux recits</span>
            </button>
            {interview && (
              <button
                onClick={handleShare}
                className="flex items-center gap-2 px-3 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Share2 className="w-5 h-5" />
                <span className="hidden sm:inline font-medium">Partager</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <main className="flex-1 pt-32">
        {isLoading ? (
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            <div className="animate-pulse space-y-6">
              <div className="aspect-[21/9] bg-gray-200 dark:bg-gray-700 rounded-2xl" />
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-8">
                <div className="space-y-6">
                  <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 space-y-4">
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-lg w-3/4" />
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 space-y-4">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-4/6" />
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 space-y-4 h-fit">
                  <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-xl w-full" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                </div>
              </div>
            </div>
          </div>
        ) : error || !interview ? (
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-red-200 dark:border-red-800/50 p-8 text-center">
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-red-500" />
              </div>
              <p className="text-red-600 dark:text-red-400 font-medium">
                {error || 'Interview non trouvee'}
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="relative h-[260px] sm:h-[360px] lg:h-[400px]">
              <img
                src={interview.cover}
                alt={interview.title}
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
                fetchPriority="high"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (!target.src.includes('fallback')) {
                    target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?q=80&w=2670&auto=format&fit=crop';
                  }
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-gray-900/50 via-gray-900/60 to-gray-900/85" />

              <div className="absolute inset-x-0 bottom-0 p-5 sm:p-8">
                <div className="max-w-5xl mx-auto">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-teal-500/90 to-cyan-500/90 backdrop-blur-md rounded-full shadow-lg">
                      <Users className="w-3.5 h-3.5 text-white" />
                      <span className="text-xs font-semibold text-white">Recit PVT</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-white/70">
                      <Calendar className="w-3.5 h-3.5" />
                      <span className="text-xs sm:text-sm">
                        {format(parseISO(interview.created), 'dd MMMM yyyy', { locale: fr })}
                      </span>
                    </div>
                  </div>

                  <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-4 leading-tight max-w-3xl">
                    {interview.title}
                  </h1>

                  {interview.shortDescription && (
                    <p className="text-sm sm:text-base text-white/80 leading-relaxed max-w-2xl line-clamp-2 mb-4">
                      {interview.shortDescription}
                    </p>
                  )}

                  <div className="flex items-center gap-3">
                    <img
                      src={interview.author.avatar || `https://ui-avatars.com/api/?name=${interview.author.firstname}&background=random`}
                      alt={interview.author.firstname}
                      className="w-8 h-8 rounded-full ring-2 ring-white/20"
                    />
                    <div>
                      <span className="block text-white font-medium text-sm">
                        Par {interview.author.firstname} {interview.author.lastname}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-10">
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6 lg:gap-8">
                <div className="min-w-0">
                  <div className="lg:hidden mb-6">
                    <IntervieweeCard interview={interview} />
                  </div>

                  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50">
                    <div className="p-4 sm:p-6 md:p-8 lg:p-10">
                      <div
                        ref={contentRef}
                        className="prose prose-base sm:prose-lg dark:prose-invert mx-auto space-y-2 max-w-none
                                   prose-img:rounded-xl prose-img:shadow-md
                                   prose-a:text-teal-600 dark:prose-a:text-teal-400 prose-a:no-underline
                                   prose-p:text-gray-700 dark:prose-p:text-gray-300
                                   prose-li:text-gray-700 dark:prose-li:text-gray-300"
                        dangerouslySetInnerHTML={{
                          __html:
                            (interview as any).sections
                              ?.filter((s: any) => s && s.content)
                              .map((s: any) => formatSection(s, interview))
                              .join('') ||
                            cleanContent(interview.content || ''),
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="hidden lg:block">
                  <div className="sticky top-36 space-y-6">
                    <IntervieweeCard interview={interview} />

                    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-sm p-5">
                      <button
                        onClick={handleShare}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        <Share2 className="w-4 h-4" />
                        Partager ce recit
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
